import type { SmoothedScalar } from "../utils/smoothing";

/**
 * Phase 1A music types.
 *
 * Harmony, motif and instrumentation state are specified in
 * docs/implementation-plan.md ch.8 but are deliberately absent here: Phase 1A
 * generates rhythm only, and a type that nothing writes is a type nobody
 * maintains. Phase 1B adds them.
 */

// ---------------------------------------------------------------------------
// Genre
// ---------------------------------------------------------------------------

/** UI values, 0..100 each, independent sliders. */
export interface GenreBlendInput {
  ambient: number;
  techno: number;
  jazz: number;
}

/** Normalised, always sums to 1.0. */
export interface GenreBlend {
  ambient: number;
  techno: number;
  jazz: number;
}

/**
 * Genre expressed as weights on generation rules — never as a choice between
 * finished patterns. Every field is 0..1 and feeds the rhythm generator.
 */
export interface GenreFeatures {
  /** How strongly a regular pulse is asserted. */
  pulseStrength: number;
  /** How closely events snap to the metric grid. */
  rhythmicRegularity: number;
  /** Chance a bar repeats the previous pattern rather than changing. */
  patternRepetition: number;
  /** How much a pattern is altered when it does change. */
  patternMutation: number;
  /** Pull towards finer subdivisions. */
  subdivisionBias: number;
  /** Weight moved from strong beats to offbeats. */
  syncopation: number;
  /** Behind-the-beat tendency, 0..1, scaled to ms downstream. */
  laybackAmount: number;
  /** Chance of quiet in-between notes. */
  ghostNoteTendency: number;
  /** Chance of a bar using a non-16 step count. */
  polymeterTendency: number;
  /** Pull towards leaving space. */
  silenceTendency: number;
  /** Baseline event count multiplier before Density is applied. */
  eventDensityBias: number;
  /** Auto-tempo target bias. Must not depend on Energy. */
  tempoBias: number;
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/**
 * Phase 1A subset. Contrast and climax exist in the full spec but need harmony
 * and instrumentation to mean anything, so they arrive with Phase 1B/1C.
 */
export type SectionName = "intro" | "establish" | "develop" | "break" | "release";

export interface SectionDefinition {
  minBars: number;
  maxBars: number;
  /** Offsets applied to the user's Energy/Density, not replacements. */
  energyOffset: number;
  densityOffset: number;
  allowedNext: Array<{ section: SectionName; weight: number }>;
}

export interface SectionState {
  name: SectionName;
  /** Bars elapsed inside the current section. */
  bar: number;
  /** Chosen length for this instance of the section. */
  length: number;
  barsSinceChange: number;
}

// ---------------------------------------------------------------------------
// Rhythm
// ---------------------------------------------------------------------------

/** Phase 1A roles. Kept deliberately small: rules matter more than voice count. */
export type RhythmRole = "kick" | "pulse" | "percussion" | "accent";

export const RHYTHM_ROLES: RhythmRole[] = ["kick", "pulse", "percussion", "accent"];

export interface StepPattern {
  /** Step count for this bar. 16 normally; 12/15/17 when polymeter kicks in. */
  stepsPerBar: number;
  /** Indices that sound. Sorted ascending, never duplicated. */
  steps: number[];
  /** Subset of `steps` that carry an accent. */
  accents: number[];
  /** Subset of `steps` played as quiet ghosts. */
  ghosts: number[];
}

export interface RoleRhythmState {
  pattern: StepPattern | null;
  /** Bars since this role's pattern last changed. */
  barsSinceMutation: number;
  /** Bars the role has been resting. 0 means active. */
  restBarsRemaining: number;
  /** Recent pattern hashes, newest last. Used to break ABAB loops. */
  recentHashes: string[];
}

export type RhythmState = Record<RhythmRole, RoleRhythmState>;

// ---------------------------------------------------------------------------
// Music state
// ---------------------------------------------------------------------------

export interface MusicState {
  bar: number;
  section: SectionState;

  energy: SmoothedScalar;
  density: SmoothedScalar;
  rhythmicComplexity: SmoothedScalar;
  grooveStrength: SmoothedScalar;
  silenceProbability: SmoothedScalar;

  genre: GenreBlend;
  features: GenreFeatures;

  rhythm: RhythmState;

  barsSinceFill: number;
  /** Per-bar report for diagnostics; overwritten every bar. */
  lastBarReport: BarReport;
}

/** What the last planned bar actually contained. Diagnostics and tests read this. */
export interface BarReport {
  bar: number;
  eventCount: number;
  activeRoles: RhythmRole[];
  averageVelocity: number;
  averageTimingOffsetMs: number;
  patternHash: string;
  wasFill: boolean;
}
